import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Sparkles, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Compact "Featured Partners" strip shown on CreatorDashboard.
 *
 * Loads the public /api/affiliate-partners list (e.g., Marblism) and
 * renders each as a small logo card that links out through our tracked
 * redirect. The goal is immediate social proof for inbound creators —
 * they see recognisable AI brands alongside their own storefront on day 1.
 *
 * Renders nothing while loading or if there are zero active partners,
 * so it degrades silently in early days.
 */
export default function FeaturedPartnersStrip() {
  const [partners, setPartners] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/affiliate-partners?limit=8&homepage_only=true`);
        if (!cancelled) setPartners(res.data.partners || []);
      } catch {
        // Silent — non-critical UI.
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!loaded || partners.length === 0) return null;

  return (
    <div
      className="bg-[#00F0FF]/5 border border-white/10 rounded-2xl p-5 md:p-6 mb-6 md:mb-8"
      data-testid="creator-featured-partners-strip"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#00F0FF]" />
          <h3 className="text-sm md:text-base font-bold text-white">
            You&rsquo;re in good company
          </h3>
          <span className="text-xs text-neutral-500">
            · featured on theailedger.ai
          </span>
        </div>
        <a
          href="/featured"
          className="text-xs text-[#00F0FF] hover:text-[#00C2CC] inline-flex items-center gap-1"
          data-testid="view-all-partners-link"
        >
          View all <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1 snap-x">
        {partners.map((p) => (
          <a
            key={p.slug}
            href={`/featured/${p.slug}`}
            className="group shrink-0 w-44 md:w-52 snap-start rounded-xl bg-neutral-900/60 border border-white/5 hover:border-[#00F0FF]/40 transition-colors p-3 flex items-center gap-3"
            data-testid={`featured-partner-${p.slug}`}
            title={p.name}
          >
            <div className="shrink-0 w-10 h-10 rounded-lg bg-black/40 border border-white/5 flex items-center justify-center overflow-hidden p-1">
              {p.logo_url ? (
                <img
                  src={p.logo_url}
                  alt={p.name}
                  className="w-full h-full object-contain"
                  loading="lazy"
                />
              ) : (
                <Sparkles className="w-4 h-4 text-[#00F0FF]" />
              )}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white truncate group-hover:text-[#00F0FF] transition-colors">
                {p.name}
              </p>
              <p className="text-[11px] text-neutral-500 truncate">
                {p.customer_benefit || p.tagline || p.category}
              </p>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}
